// src/api/supabaseClient.ts
import { authStorageKeyFromSupabaseUrl } from "@/auth/authStorageKey";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { Platform } from "react-native";

export type SupabaseConfigError = {
  missing: readonly string[];
  message: string;
};

function isBlank(value: string | undefined | null): boolean {
  return typeof value !== "string" || value.trim() === "";
}

/**
 * Leere Konfiguration ist ein definierter Fehlerzustand.
 * createClient("", "") wirft sonst `supabaseUrl is required` und die App bleibt weiß.
 * Die Meldung nennt nur Variablennamen, nie die Werte.
 */
export function supabaseConfigErrorFrom(
  url: string | undefined | null,
  anonKey: string | undefined | null,
): SupabaseConfigError | null {
  const missing: string[] = [];
  if (isBlank(url)) missing.push("EXPO_PUBLIC_SUPABASE_URL");
  if (isBlank(anonKey)) missing.push("EXPO_PUBLIC_SUPABASE_ANON_KEY");
  if (missing.length === 0) return null;

  const names = missing.join(", ");
  const message =
    missing.length === 1
      ? `Die App wurde ohne Supabase-Konfiguration gebaut. Es fehlt die Build-Variable ${names}. Bitte in Cloudflare Pages für Production und Preview setzen und neu bauen.`
      : `Die App wurde ohne Supabase-Konfiguration gebaut. Es fehlen die Build-Variablen ${names}. Bitte in Cloudflare Pages für Production und Preview setzen und neu bauen.`;

  return { missing, message };
}

export function createSupabaseAccess(
  url: string | undefined | null,
  anonKey: string | undefined | null,
  create: typeof createClient = createClient,
): { configError: SupabaseConfigError | null; client: SupabaseClient } {
  const configError = supabaseConfigErrorFrom(url, anonKey);
  const resolvedUrl = typeof url === "string" ? url : "";
  const resolvedAnonKey = typeof anonKey === "string" ? anonKey : "";

  if (configError) {
    console.error(configError.message);
  }

  let _client: SupabaseClient | null = null;

  function getClient(): SupabaseClient {
    if (configError) {
      throw new Error(configError.message);
    }
    if (!_client) {
      _client = create(resolvedUrl, resolvedAnonKey, {
        auth: {
          storage: Platform.OS === "web" ? undefined : AsyncStorage,
          autoRefreshToken: true,
          persistSession: true,
          // PKCE für Code-Flows. Recovery tauscht token_hash erst auf Knopfdruck,
          // deshalb darf die URL die Session nicht selbst einlösen.
          flowType: "pkce",
          detectSessionInUrl: false,
        },
      });
    }
    return _client;
  }

  // Lazy-Export: vermeidet "Cannot access 'supabase' before initialization"
  // bei zirkulären Imports (z. B. im Webpack-Web-Bundle).
  const client = new Proxy({} as SupabaseClient, {
    get(_, prop) {
      const resolved = getClient();
      const val = (resolved as unknown as Record<string | symbol, unknown>)[prop];
      if (typeof val === "function") return val.bind(resolved);
      return val;
    },
  });

  return { configError, client };
}

/**
 * Key, den supabase-js aus der Projekt-URL bildet, wenn kein storageKey gesetzt ist.
 * Storage: Web lässt auth-js localStorage nehmen (wir übergeben undefined),
 * nativ ist es AsyncStorage — dieselbe Instanz wie im createClient unten.
 */
export function currentAuthStorageKey(): string | null {
  const url = process.env.EXPO_PUBLIC_SUPABASE_URL;
  if (isBlank(url)) return null;
  try {
    return authStorageKeyFromSupabaseUrl(url as string);
  } catch {
    return null;
  }
}

export function currentAuthStorage(): {
  getItem?: (key: string) => unknown;
  removeItem: (key: string) => unknown;
} {
  if (
    Platform.OS === "web" &&
    typeof globalThis !== "undefined" &&
    typeof globalThis.localStorage !== "undefined"
  ) {
    return globalThis.localStorage;
  }
  return AsyncStorage;
}

const access = createSupabaseAccess(
  process.env.EXPO_PUBLIC_SUPABASE_URL,
  process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY,
);

export const supabaseConfigError = access.configError;
export const supabase = access.client;
